/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from 'helpers/store'
import {closeSecondaryModal} from 'helpers/actions'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import {PackagesCartItem} from 'types/entities/PackagesCartItem'
import UiTextarea from 'components/Ui/UiTextarea'
import UiCounter from 'components/Ui/UiCounter'
import UiCurrencyInput from 'components/Ui/UiCurrencyInput'
import SellerAction from 'store/seller/SellerAction'
import {toast} from 'utils/toastUtils'
import {Controller, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {translate} from 'utils/utility'

const editItemSchema = z.object({
    quantity: z.number().min(0),
    price: z.number().min(0),
    custom_text: z.string().optional(),
})

type EditItemFormData = z.infer<typeof editItemSchema>

interface BookingSellerEditReservationItemModalProps {
    reservationId: number
    item: PackagesCartItem
    onSuccess?: () => void
}

const BookingSellerEditReservationItemModal: React.FC<
    BookingSellerEditReservationItemModalProps
> = ({ reservationId, item, onSuccess }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { isSubmitting, errors },
    } = useForm<EditItemFormData>({
        resolver: zodResolver(editItemSchema),
        defaultValues: {
            quantity: item.drivers_count_adult + item.drivers_count_child,
            price: (item.price || 0) / 100,
            custom_text: item.custom_text || '',
        },
    })

    const quantity = watch('quantity')
    const price = watch('price')
    const totalPrice = quantity * price

    const onSubmit = async (
        data: EditItemFormData,
        closeAfterSave: boolean = true
    ) => {
        try {
            // Update quantity
            if (
                data.quantity !==
                item.drivers_count_adult + item.drivers_count_child
            ) {
                await dispatch(
                    SellerAction.fetchChangeUpsellPriceAndQuantity(
                        reservationId,
                        item,
                        'quantity',
                        data.quantity
                    )
                )
            }

            // Update price
            if (data.price !== (item.price || 0) / 100) {
                await dispatch(
                    SellerAction.fetchChangeUpsellPriceAndQuantity(
                        reservationId,
                        item,
                        'price',
                        Math.round(data.price * 100)
                    )
                )
            }

            // Update custom text
            if (data.custom_text !== item.custom_text) {
                await dispatch(
                    SellerAction.fetchChangeUpsellPriceAndQuantity(
                        reservationId,
                        item,
                        'custom_text',
                        data.custom_text || ''
                    )
                )
            }

            toast.success(t('Item updated successfully'))
            if (closeAfterSave) {
                dispatch(closeSecondaryModal())
            }
            if (onSuccess) onSuccess()
        } catch (e) {
            console.error('Error updating item:', e)
            toast.error(t('Failed to update item'))
        }
    }

    const onError = () => {
        toast.error(t('Please fix validation errors'))
    }

    const itemName = item.packageItem?.name
        ? translate(item.packageItem.name)
        : t('Unknown Item')

    return (
        <UiFormModalShell title={t('Edit Item')}>
            <form
                onSubmit={handleSubmit((data) => onSubmit(data, true), onError)}
                className="h-full flex flex-col"
            >
                <div className="p-4 flex-1 overflow-auto">
                    <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {t('Item')}
                        </div>
                        <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                            {item.drivers_count_adult +
                                item.drivers_count_child}{' '}
                            x {itemName}
                        </div>
                    </div>

                    <UiFormInner title={t('Item Details')}>
                        <UiFormGrid cols={3}>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {t('Quantity')}
                                </label>
                                <Controller
                                    name="quantity"
                                    control={control}
                                    render={({ field }) => (
                                        <UiCounter
                                            value={field.value}
                                            onChange={(val) =>
                                                field.onChange(
                                                    val === '' ? 0 : val
                                                )
                                            }
                                            min={0}
                                        />
                                    )}
                                />
                                {errors.quantity && (
                                    <span className="text-xs text-red-500">
                                        {errors.quantity.message}
                                    </span>
                                )}
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <Controller
                                    name="price"
                                    control={control}
                                    render={({ field }) => (
                                        <UiCurrencyInput
                                            label={t('Unit Price')}
                                            value={field.value}
                                            onValueChange={(
                                                value: string | undefined
                                            ) => {
                                                const numValue =
                                                    value === undefined
                                                        ? 0
                                                        : parseFloat(value)
                                                field.onChange(
                                                    isNaN(numValue)
                                                        ? 0
                                                        : numValue
                                                )
                                            }}
                                            prefix="CHF "
                                            decimalsLimit={2}
                                            error={errors.price?.message}
                                        />
                                    )}
                                />
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <UiCurrencyInput
                                    label={t('Total Price')}
                                    value={totalPrice}
                                    prefix="CHF "
                                    decimalsLimit={2}
                                    disabled={true}
                                />
                            </div>
                        </UiFormGrid>
                    </UiFormInner>

                    <UiFormInner
                        title={t('Custom Text')}
                        actions={
                            <button
                                type="button"
                                onClick={() => {
                                    const description = item.packageItem
                                        ?.custom_text
                                        ? translate(
                                              item.packageItem.custom_text
                                          )
                                        : itemName
                                    setValue('custom_text', description)
                                }}
                                className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1 transition-colors"
                            >
                                {t('Auto fill')}
                            </button>
                        }
                    >
                        <UiFormGrid cols={1}>
                            <Controller
                                name="custom_text"
                                control={control}
                                render={({ field }) => (
                                    <UiTextarea
                                        {...field}
                                        placeholder={t('Enter custom text...')}
                                        rows={10}
                                        error={errors.custom_text?.message}
                                    />
                                )}
                            />
                        </UiFormGrid>
                    </UiFormInner>
                </div>
                <UiFormActionsBar
                    primaryText={t('Save')}
                    onPrimary={handleSubmit(
                        (data) => onSubmit(data, false),
                        onError
                    )}
                    saveAndCloseText={t('Save and close')}
                    onSaveAndClose={handleSubmit(
                        (data) => onSubmit(data, true),
                        onError
                    )}
                    loading={isSubmitting}
                />
            </form>
        </UiFormModalShell>
    )
}

export default BookingSellerEditReservationItemModal
